import React from 'react';
import { Typography, Box, Card, CardContent, Link, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { JournalEntry } from '../../types/journal';

const moodColors: Record<string, string> = {
    happy: '#305548', // deep green, positive
    calm: '#B3C5BE', // soft green, calming
    anxious: '#A86E64', // muted red, tension
    stressed: '#A86E64', // muted red, tension
    excited: '#D4B6AD', // warm, energetic
    neutral: '#C7C4BC', // medium light, neutral
    sad: '#111', // soft, muted
    angry: '#A86E64' // muted red, anger
};

interface JournalItemProps {
    entry: JournalEntry;
}

const JournalItem: React.FC<JournalItemProps> = ({ entry }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const formatDate = (date: Date) => {
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const locale = i18next.language === 'pt-BR' ? ptBR : enUS;
        const timeFormat = 'h:mm a'; // AM/PM time format

        if (diffDays <= 7) {
            return format(date, `EEEE, ${timeFormat}`, { locale });
        } else if (date.getFullYear() === now.getFullYear()) {
            return format(date, `MMMM d, ${timeFormat}`, { locale });
        } else {
            return format(date, `MMMM d, yyyy, ${timeFormat}`, { locale });
        }
    };

    return (
        <Box sx={{ mb: 1 }}>
            <Link
                onClick={() => navigate(`/journal/${entry._id}`)}
                underline="none"
                sx={{ cursor: 'pointer' }}
            >
                <Card sx={{
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    '&:hover': {
                        backgroundColor: (theme) => theme.palette.background.paper,
                        transition: 'background-color 0.3s ease-in-out'
                    }
                }}>
                    <CardContent sx={{
                        padding: { xs: 2, sm: 3 },
                        '&:last-child': { pb: { xs: 2, sm: 3 } }
                    }}>
                        <Box display="flex" alignItems="flex-start" gap={2}>
                            <Box
                                flexShrink={0}
                                sx={{
                                    width: '80px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <Chip
                                    label={t(`journal.moods.${entry.mood}`)}
                                    size="small"
                                    sx={{
                                        backgroundColor: moodColors[entry.mood] || '#305548',
                                        color: '#fff',
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                        height: '24px',
                                        borderRadius: '12px',
                                        textTransform: 'capitalize',
                                        letterSpacing: '0.3px'
                                    }}
                                />
                            </Box>
                            <Box flexGrow={1} sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mt: 0,
                                        mb: 1,
                                        fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                        fontWeight: 600,
                                        color: '#2C2C2C',
                                        fontFamily: '"Inter", sans-serif',
                                        letterSpacing: '0.2px'
                                    }}
                                >
                                    {formatDate(new Date(entry.createdAt))}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        mt: 0,
                                        color: '#666',
                                        lineHeight: 1.6,
                                        fontSize: { xs: '0.875rem', sm: '0.9rem' },
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 400
                                    }}
                                >
                                    {entry.content.length > 100
                                        ? `${entry.content.substring(0, 100)}...`
                                        : entry.content}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Link>
            {(
                <Box
                    sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        width: '100%',
                        mt: 0
                    }}
                />
            )}
        </Box>
    );
};

export default JournalItem; 